//frontend/src/actions/productActions.js
// frontend/src/actions/productActions.js (updated for egg products with per dozen pricing)

import axios, { publicInstance } from '../utils/axiosConfig';
import { productsSuccess, productsFail, productsRequest, clearProducts } from "../slices/productsSlice";
import { productSuccess, productFail, productRequest } from '../slices/productSlice';
import {
    featuredProductsRequest,
    featuredProductsSuccess,
    featuredProductsFail,
    clearFeaturedProducts
} from '../slices/featuredProductsSlice';

export const getProducts = (keyword = '', price, category, currentPage = 1, forceRefresh = false) => async (dispatch) => {
    try {
        console.log('getProducts action called', { keyword, price, category, currentPage, forceRefresh });
        
        // Clear existing products if force refresh
        if (forceRefresh) {
            dispatch(clearProducts());
        }
        
        dispatch(productsRequest());
        
        // Build the API URL with query parameters
        let link = `/products?page=${currentPage}`;
        if(keyword) {
            link += `&keyword=${encodeURIComponent(keyword)}`;
        }
        if(price && Array.isArray(price) && price.length === 2) {
            link += `&price[gte]=${price[0]}&price[lte]=${price[1]}`;
        }
        if(category) {
            link += `&category=${encodeURIComponent(category)}`;
        }
        
        // Add force refresh parameter
        if (forceRefresh) {
            link += `&_force=${Date.now()}`;
        }
        
        console.log('Fetching products with URL:', link);
        const { data } = await publicInstance.get(link);
        console.log('Products API response:', data);
        console.log('Products data:', data.products?.map(p => ({ 
            id: p._id, 
            name: p.name, 
            isVisible: p.isVisible 
        })));
        
        // Ensure image URLs are properly formatted
        if (data.products && Array.isArray(data.products)) {
            console.log('Dispatching productsSuccess with', data.products.length, 'products');
            data.products = data.products.map(product => {
                if (product.images && Array.isArray(product.images)) {
                    // Images are already processed in the axios interceptor
                }
                return product;
            });
        }
        
        dispatch(productsSuccess({
            products: data.products,
            count: data.productsCount,
            resPerPage: data.resPerPage,
            filteredProductsCount: data.filteredProductsCount
        }));
    } catch (error) {
        console.error('Error in getProducts:', error);
        dispatch(productsFail(error.response?.data?.message || error.message));
    }
};

// Get Single Product Action
export const getProduct = (id, forceRefresh = false) => async (dispatch) => {
    try {
        dispatch(productRequest());
        
        let url = `/product/${id}`;
        if (forceRefresh) {
            url += `?_force=${Date.now()}`;
        }
        
        console.log('Fetching single product with URL:', url);
        const response = await publicInstance.get(url);
        console.log('Single product API Response:', response.data);
        console.log('Product price type details:', {
            id: response.data.product._id,
            name: response.data.product.name,
            priceType: response.data.product.priceType,
            price: response.data.product.price,
            weightOptions: response.data.product.weightOptions
        });
        // Image URLs are processed in the axios interceptor
        
        dispatch(productSuccess({
            product: response.data.product
        }));
    } catch (error) {
        console.error('API Error getting single product:', error);
        dispatch(productFail({
            message: error.response?.data?.message || error.message
        }));
    }
};

// Add to Cart Action - Updated to handle per dozen pricing for eggs
export const addCartItem = (id, quantity, selectedWeight) => async (dispatch) => {
    try {
        dispatch({ type: 'ADD_TO_CART_REQUEST' });
        const { data } = await axios.get(`/product/${id}`);
        
        // Determine the base price based on product type
        let originalPrice = 0;
        let priceType = data.product.priceType;
        
        if (priceType === 'perDozen') {
            // For egg products priced per dozen
            originalPrice = data.product.dozenPrice || 0;
        } else if (priceType === 'fixed') {
            // For products with weight options (chicken, mutton)
            originalPrice = data.product.weightOptions.find(opt => opt.weight === selectedWeight)?.price || 0;
        } else {
            // For per kg products
            originalPrice = data.product.price || 0;
        }
        
        // Calculate final price with any discounts
        let finalPrice = originalPrice;
        if (data.product.discount?.active) {
            if (data.product.discount.type === 'percentage') {
                finalPrice = originalPrice - (originalPrice * data.product.discount.value / 100);
            } else {
                finalPrice = originalPrice - data.product.discount.value;
            }
        }
        
        // Make sure we have a properly formatted image URL
        const imageUrl = data.product.images && data.product.images[0]
            ? data.product.images[0].image
            : 'https://via.placeholder.com/400x300?text=Image+Not+Available';
        
        dispatch({
            type: 'ADD_TO_CART_SUCCESS',
            payload: {
                product: data.product._id,
                name: data.product.name,
                price: originalPrice,
                image: imageUrl,
                weight: selectedWeight,
                quantity,
                discount: data.product.discount,
                priceType: data.product.priceType, // Adding priceType to payload
                dozenPrice: data.product.dozenPrice, // Adding dozenPrice for egg products
                discountedPrice: finalPrice,
                category: data.product.category // Adding category for reference
            }
        });
    } catch (error) {
        dispatch({
            type: 'ADD_TO_CART_FAIL',
            payload: error.response?.data?.message || error.message
        });
    }
};

// Get Featured Products Action
export const getFeaturedProducts = (forceRefresh = false) => async (dispatch) => {
    try {
        // Clear existing featured products if force refresh
        if (forceRefresh) {
            dispatch(clearFeaturedProducts());
        }
        
        dispatch(featuredProductsRequest());
        
        // The correct endpoint path without '/api/v1' prefix
        // This works because your axios config already adds the base URL
        let url = '/products/featured';
        if (forceRefresh) {
            url += `?_force=${Date.now()}`;
        }
        
        console.log('Fetching featured products with URL:', url);
        const { data } = await publicInstance.get(url);
        
        console.log('Featured products API response:', data);
        console.log('Featured products data:', data.featuredProducts?.map(p => ({ 
            id: p._id, 
            name: p.name, 
            isVisible: p.isVisible 
        })));
        
        if (data.featuredProducts) {
            console.log('Dispatching featuredProductsSuccess with', data.featuredProducts.length, 'products');
            dispatch(featuredProductsSuccess(data.featuredProducts));
        } else {
            console.log('No featured products found, dispatching error');
            dispatch(featuredProductsFail('No featured products returned from API'));
        }
    } catch (error) {
        console.error('Error fetching featured products:', error);
        dispatch(featuredProductsFail(error.response?.data?.message || error.message));
    }
};
